import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Building2, ShieldAlert, Sparkles, Wrench, Activity, Network, Receipt } from "lucide-react";
import AboutHeroArt from "@/components/AboutHeroArt";

// ---- Operating principles (gradient cards, Insights aesthetic) -----------
const PRINCIPLES = [
  {
    num: "01",
    title: "Instrumented, human-judged",
    body: "Operators with deep payments and revenue-stack experience make every call that matters. Tools accelerate the synthesis; judgment owns the recommendation. AI on enterprise tiers, contractually zero-training.",
    from: "#0a1628",
    to: "#1e3a8a",
    pattern: "grid",
    Icon: Activity,
  },
  {
    num: "02",
    title: "Revenue, defended",
    body: "Every recommendation ships with the math behind it — revenue lift, basis points saved, vendor cost delta. We model what your data supports, never more.",
    from: "#1e3a8a",
    to: "#6d28d9",
    pattern: "diag",
    Icon: Receipt,
  },
  {
    num: "03",
    title: "Artifacts, not advice",
    body: "Each engagement ends in a tangible, owner-shippable deliverable. Not a deck. Not a memo. A working asset with actionable items.",
    from: "#6d28d9",
    to: "#ac62ea",
    pattern: "dots",
    Icon: Network,
  },
  {
    num: "04",
    title: "Named accountability",
    body: "A named senior operator owns your engagement end-to-end. Trained on the new way of consulting: instrumented, accountable, fast. No handoffs, no B-team after kickoff.",
    from: "#ac62ea",
    to: "#5d2d8f",
    pattern: "diag",
    Icon: ShieldCheck,
  },
];

// ---- Disciplines (gradient mini-covers, Insights aesthetic) --------------
const DISCIPLINES = [
  {
    Icon: Building2,
    num: "01",
    title: "Payments & acceptance",
    note: "Authorization lift, intelligent routing, BIN strategy, MCC and AFT/OCT mandate alignment, and the regulatory posture work that keeps acquirers and schemes on your side.",
    from: "#0a1628",
    to: "#ac62ea",
    pattern: "grid",
  },
  {
    Icon: ShieldAlert,
    num: "02",
    title: "Fraud & compliance",
    note: "Rules engineering, 3DS optimization, scheme-program exits, velocity controls, and the chargeback hygiene work that protects acceptance without throttling good revenue.",
    from: "#312e81",
    to: "#6d28d9",
    pattern: "diag",
  },
  {
    Icon: Wrench,
    num: "03",
    title: "Experience & engineering",
    note: "Checkout UX, declined-transaction recovery, multi-PSP API orchestration, and the stack-design rationalization that turns a fragmented payments architecture into a working revenue surface.",
    from: "#6d28d9",
    to: "#ac62ea",
    pattern: "dots",
  },
];

const PATTERN_STYLES = {
  grid: {
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
    backgroundSize: "24px 24px",
  },
  diag: {
    backgroundImage:
      "repeating-linear-gradient(135deg, rgba(255,255,255,0.07) 0 1px, transparent 1px 12px)",
  },
  dots: {
    backgroundImage:
      "radial-gradient(circle, rgba(255,255,255,0.14) 1px, transparent 1.5px)",
    backgroundSize: "18px 18px",
  },
};

export default function About() {
  return (
    <div className="bg-white" data-testid="about-page">
      {/* ---- HERO ------------------------------------------------------- */}
      <section className="bg-ink text-white relative overflow-hidden group" data-testid="about-hero">
        {/* Full-bleed atmospheric backdrop */}
        <AboutHeroArt />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 relative">
          <div className="lg:max-w-3xl">
            <div className="label-mono" style={{ color: "rgba(255,255,255,0.5)" }}>About</div>
            <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl font-semibold mt-5 tracking-[-0.04em] leading-[0.96]">
              We named the studio<br />after a footnote.
            </h1>
            <p className="text-lg text-white/65 mt-7 max-w-xl leading-relaxed">
              A short story about a word, and the reason it&apos;s on our door.
            </p>
          </div>
        </div>
      </section>

      {/* ---- DEFINITION + NARRATIVE ------------------------------------- */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 prose-content">
          <div className="label-mono">Definition</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 font-semibold tracking-[-0.03em] leading-tight">
            Side B <span className="text-muted-foreground font-normal">/sʌɪd biː/</span>
          </h2>
          <p className="text-muted-foreground mt-3 italic">noun.</p>
          <ol className="mt-6 space-y-4 text-lg leading-relaxed">
            <li><span className="font-medium text-foreground">1.</span> The reverse face of a recording where artists historically placed material that didn&apos;t fit the obvious commercial format — experiments, alternate takes, the work that ended up mattering more than the hit.</li>
            <li><span className="font-medium text-foreground">2.</span> A useful posture for a revenue team: the place you put the unproven idea, the new motion, the customer segment everyone else ignored.</li>
          </ol>

          <div className="hairline my-14" />

          <div className="label-mono">Why it matters here</div>
          <h3 className="font-display text-2xl mt-3 font-semibold tracking-tight">A different posture for revenue work.</h3>
          <div className="space-y-5 mt-5 text-base text-foreground/85 leading-relaxed">
            <p>
              Most revenue consulting sells the safe answer — a benchmark deck, a scaffolded playbook, a model that looks great in a board pack. That&apos;s the A-side. It&apos;s rarely what moves the number.
            </p>
            <p>
              SideB exists because the real growth in a payments and revenue org almost always comes from the Side B: the acquirer cost review nobody has time to run, the orchestration vendor playbook gathering dust in procurement, the RevOps lead you can&apos;t justify hiring full-time. Unglamorous on paper. Compounding in practice.
            </p>
            <p>
              We&apos;re an AI-first studio by design — modern instrumentation paired with a small, named team that&apos;s been on the operator side of those decisions. One strategy, one accountable line of sight from diagnosis to deliverable. Engagements are fixed-scope and fixed-price. If we&apos;re not a fit, you still leave with a clearer map of what your revenue motion actually needs.
            </p>
          </div>
        </div>
      </section>

      {/* ---- OPERATING PRINCIPLES (gray-bordered, in prose container) -- */}
      <section className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 prose-content">
          <div className="label-mono">Operating principles</div>
          <h3 className="font-display text-2xl mt-3 font-semibold tracking-tight">Four rules we work by.</h3>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRINCIPLES.map((p) => (
              <div
                key={p.num}
                className="border border-border rounded-xl p-5"
                data-testid={`operating-principle-${p.num}`}
              >
                <div className="font-mono text-[11px] text-[#ac62ea] tracking-widest font-semibold">{p.num}</div>
                <div className="font-display text-lg mt-2 font-semibold">{p.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- THE TEAM --------------------------------------------------- */}
      <section className="bg-white border-b border-border" data-testid="about-team-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 prose-content">
          <div className="label-mono">The team</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 font-semibold tracking-[-0.03em] leading-tight">
            Fintech, Agentic, and RevOps nerds at <span className="text-[#ac62ea]">your fingertips.</span>
          </h2>
          <p className="mt-5 text-base text-foreground/85 leading-relaxed max-w-3xl">
            <b>70+ years</b> of collective, real-world expertise — decision-makers and problem-solvers who&apos;ve scaled some of the world&apos;s most influential tech, fintech, and global consumer brands. From SMBs in hypergrowth to global enterprise, we have the bench to steer the work.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="team-trust-pills">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-white text-xs font-medium text-foreground/80">
                <ShieldCheck className="h-3.5 w-3.5 text-[#ac62ea]" /> Background-checked
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#ac62ea]/30 bg-[#ac62ea]/10 text-xs font-semibold text-[#7d3fbf]" data-testid="team-woman-owned-pill">
                <Sparkles className="h-3.5 w-3.5" /> Woman-owned
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-white text-xs font-medium text-foreground/80">
                <Building2 className="h-3.5 w-3.5 text-[#ac62ea]" /> SMB → Enterprise
              </span>
            </div>

            {/* Disciplines — gradient mini-covers */}
            <div className="mt-12 lg:mt-16">
            <div className="label-mono">Disciplines on the bench</div>
            <div className="mt-5 grid sm:grid-cols-3 gap-4 lg:gap-5" data-testid="team-disciplines">
              {DISCIPLINES.map((d) => (
                <div
                  key={d.title}
                  className="relative overflow-hidden rounded-2xl text-white p-6 lg:p-7 group transition-transform duration-300 hover:-translate-y-0.5 aspect-[5/4]"
                  data-testid={`team-discipline-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  style={{ backgroundImage: `linear-gradient(135deg, ${d.from} 0%, ${d.to} 100%)` }}
                >
                  <div className="absolute inset-0 opacity-90" style={PATTERN_STYLES[d.pattern]} aria-hidden />
                  <div
                    className="absolute inset-0"
                    style={{ background: "radial-gradient(120% 120% at 0% 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.40) 100%)" }}
                    aria-hidden
                  />
                  <d.Icon
                    className="absolute -right-6 -bottom-6 h-36 w-36 text-white/15 transition-transform duration-700 ease-out group-hover:rotate-[8deg] group-hover:scale-110 group-hover:text-white/25"
                    strokeWidth={1.2}
                    aria-hidden
                  />
                  <div className="relative z-10 h-full flex flex-col">
                    <div className="font-mono text-[11px] text-white/70 tracking-widest font-semibold">{d.num}</div>
                    <div className="font-display text-xl lg:text-2xl mt-2 font-semibold tracking-tight leading-snug">{d.title}</div>
                    <div className="text-xs lg:text-sm text-white/75 mt-2 leading-relaxed">{d.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Two-audience row */}
          <div className="mt-12 lg:mt-16 grid sm:grid-cols-2 gap-4 lg:gap-5">
            <div className="rounded-xl border border-border bg-white p-6 hover:border-[#ac62ea]/40 hover:shadow-[0_12px_30px_-12px_rgba(11,30,90,0.15)] transition-all" data-testid="team-audience-crisis">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-[#ac62ea]" />
                <div className="label-mono">Got an immediate issue?</div>
              </div>
              <p className="mt-3 text-sm text-foreground/85 leading-relaxed">Scheme program exits, fraud spikes, frozen reserves, vendor renewals you don&apos;t trust — we step in fast and own the resolution end-to-end.</p>
            </div>
            <div className="rounded-xl border border-border bg-white p-6 hover:border-[#ac62ea]/40 hover:shadow-[0_12px_30px_-12px_rgba(11,30,90,0.15)] transition-all" data-testid="team-audience-proactive">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#ac62ea]" />
                <div className="label-mono">Want to prevent one?</div>
              </div>
              <p className="mt-3 text-sm text-foreground/85 leading-relaxed">Acceptance audits, orchestration reviews, BI-stack rationalization, compliance posture work — we install the guardrails before the next quarter exposes them.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---- CLOSING CTA ------------------------------------------------ */}
      <section className="bg-[#f6f1fb]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.03em]">Ready to flip the record?</h3>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Book a discovery session, or send us a brief. Either way you&apos;ll have<br />a clearer plan in 48 hours.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] h-11 px-6" data-testid="about-services-cta">
              <Link to="/services">See engagements <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill h-11 px-6" data-testid="about-inquiry-cta">
              <Link to="/inquiry">Send a brief</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
